import { forwardRef } from 'react';
import styled from 'styled-components';

import type { InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Error message — replaces helperText and triggers error styling */
  error?: string;
  /** Whether the input stretches to full container width */
  fullWidth?: boolean;
  /** Icon element rendered inside the input (left side) */
  icon?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, fullWidth, icon, className, ...rest }, ref) => {
    return (
      <InputWrapper className={className} $fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <InputContainer $hasError={!!error} $hasIcon={!!icon}>
          {icon && <IconWrapper>{icon}</IconWrapper>}
          <StyledInput ref={ref} $hasIcon={!!icon} {...rest} />
        </InputContainer>
        {(error || helperText) && (
          <HelperText $isError={!!error}>{error || helperText}</HelperText>
        )}
      </InputWrapper>
    );
  },
);

Input.displayName = 'Input';

export default Input;

const InputWrapper = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

// A field label names its value, so it stays a step below it: at --text-sm it
// matched the value's own size and weight and read as a heading.
const Label = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

const InputContainer = styled.div<{ $hasError?: boolean; $hasIcon?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--input-background);
  border: 1px solid
    ${props => (props.$hasError ? 'var(--destructive)' : 'var(--border)')};
  border-radius: var(--radius-input);
  transition: border-color var(--transition-fast);

  /* Soft escalation below the focus state (docs/design-system.md → Form Fields). */
  &:hover:not(:focus-within) {
    border-color: ${props =>
      props.$hasError ? 'var(--destructive)' : 'var(--text-muted)'};
  }

  &:focus-within {
    border-color: ${props =>
      props.$hasError ? 'var(--destructive)' : 'var(--focus-border)'};
    box-shadow: 0 0 0 1px
      ${props => (props.$hasError ? 'var(--destructive)' : 'var(--focus-ring)')};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: var(--spacing-md);
  color: var(--text-muted);
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StyledInput = styled.input<{ $hasIcon?: boolean }>`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  padding-left: ${props =>
    props.$hasIcon ? 'var(--spacing-sm)' : 'var(--spacing-md)'};
  background: transparent;
  border: none;
  outline: none;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--foreground);

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelperText = styled.span<{ $isError?: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  color: ${props =>
    props.$isError ? 'var(--destructive)' : 'var(--text-muted)'};
`;
